import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../ui/use-toast';
import { type User } from '../../services/staff';
import { scheduleEventsService } from '../../services/schedule-events';
import { ScheduleUserSearch } from './ScheduleUserSearch';

interface ScheduleBulkRegisterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymSlug: string;
  eventId: string | null;
  eventTitle: string;
  /** Pre-fills the until date (the recurring event's end), if any. */
  untilDefault?: string | null;
  onDone: () => void;
}

/**
 * Admin-only: enrol a searched user in every future occurrence of a recurring
 * event up to a chosen end date ("iscrivi al corso fino a…").
 */
export function ScheduleBulkRegisterDialog({
  open,
  onOpenChange,
  gymSlug,
  eventId,
  eventTitle,
  untilDefault,
  onDone,
}: ScheduleBulkRegisterDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [selected, setSelected] = useState<User | null>(null);
  const [untilDate, setUntilDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setUntilDate(untilDefault ?? '');
  }, [open, untilDefault]);

  const handleSubmit = async () => {
    if (!eventId || !selected || !untilDate) return;
    setSaving(true);
    try {
      const result = await scheduleEventsService.bulkRegister(gymSlug, eventId, {
        user_slug: selected.slug,
        until_date: untilDate,
      });
      toast({
        title: t('schedule.bulkRegister.success', {
          count: result.registered_count,
          name: selected.full_name || selected.username,
        }),
      });
      onDone();
      onOpenChange(false);
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : t('errors.unexpectedError'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('schedule.bulkRegister.title', { title: eventTitle })}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('schedule.bulkRegister.userLabel')}</Label>
            <ScheduleUserSearch selected={selected} onSelect={setSelected} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bulk-register-until">{t('schedule.bulkRegister.untilLabel')}</Label>
            <Input
              id="bulk-register-until"
              type="date"
              value={untilDate}
              onChange={e => setUntilDate(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={saving || !selected || !untilDate}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('schedule.bulkRegister.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
